import { useState } from "react";
import { Link } from "react-router-dom";
import { displayAdminEventLabel, displayEventTime } from "@/lib/datetime";
import { Pencil, Archive, ArchiveRestore, X, Trash2, Undo2, ArrowUpDown, Sparkles } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Event } from "@/types/event";
import { cn } from "@/lib/utils";
import AdminEventBookings from "@/components/admin/AdminEventBookings";
import { EventSortField, SortDirection } from "@/hooks/useAdminEvents";
import { FirstTimerFollowupsDialog } from "@/components/admin/first-timer-followups/FirstTimerFollowupsDialog";

interface EventListTableProps {
  events: Event[];
  isEmpty: boolean;
  onEditEvent: (event: Event) => void;
  onDeleteTag: (eventId: string, tag: string) => void;
  onToggleArchiveEvent: (eventId: string, isArchived: boolean) => void;
  onToggleCancelEvent: (eventId: string, isCanceled: boolean, reason: string | null) => void;
  currentStatus: string | null;
  sortField: EventSortField;
  sortDirection: SortDirection;
  onSort: (field: EventSortField) => void;
}

const EventListTable = ({
  events,
  isEmpty,
  onEditEvent,
  onDeleteTag,
  onToggleArchiveEvent,
  onToggleCancelEvent,
  currentStatus,
  sortField,
  sortDirection,
  onSort,
}: EventListTableProps) => {
  const [followupsEventId, setFollowupsEventId] = useState<string | null>(null);

  const handleToggleCancelEvent = async (eventId: string, isCanceled: boolean) => {
    const reason = isCanceled ? prompt("Enter reason for canceling event", "") : null;
    onToggleCancelEvent(eventId, isCanceled, reason);
  };

  const now = Date.now();

  const SortableHeader = ({
    field,
    children,
    className,
  }: {
    field: EventSortField;
    children: React.ReactNode;
    className?: string;
  }) => (
    <TableHead
      className={cn("cursor-pointer hover:bg-muted/50 transition-colors", className)}
      onClick={() => onSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        <ArrowUpDown
          className={cn(
            "h-3 w-3",
            sortField === field ? "text-secondary" : "text-muted-foreground"
          )}
        />
      </div>
    </TableHead>
  );

  return (
    <>
    <Table>
      <TableHeader>
        <TableRow className="bg-muted">
          <SortableHeader field="title">Title</SortableHeader>
          <TableHead>Status</TableHead>
          <SortableHeader field="start_time" className="text-right">Date</SortableHeader>
          <SortableHeader field="venue">Venue</SortableHeader>
          <SortableHeader field="booked" className="text-right">Booked</SortableHeader>
          <SortableHeader field="capacity" className="text-right">Capacity</SortableHeader>
          <SortableHeader field="price" className="text-right">Price</SortableHeader>
          <TableHead>Tags</TableHead>
          {currentStatus === "canceled" && <TableHead>Reason</TableHead>}
          <TableHead className="text-center">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {events?.map((event) => (
          <>
            <TableRow key={event.id} className="h-9">
              <TableCell className="font-medium py-1 whitespace-nowrap max-w-[200px] truncate">
                {displayAdminEventLabel(event.title, event.start_time, event.timezone)}
              </TableCell>
              <TableCell className="py-1">
                <div className="flex flex-col gap-1 items-start">
                  {event.is_canceled ? (
                    <Badge variant="destructive" className="text-xs">Canceled</Badge>
                  ) : event.is_archived ? (
                    <Badge variant="secondary" className="text-xs">Archived</Badge>
                  ) : (
                    <Badge variant="default" className="text-xs bg-success">Active</Badge>
                  )}
                  {event.start_time && new Date(event.start_time).getTime() < now && event.attendance && event.attendance.confirmedTickets > 0 && (
                    <Link
                      to={`/events/${event.id}/checkin`}
                      className={cn(
                        "text-[10px] px-1.5 py-0.5 rounded border whitespace-nowrap hover:opacity-80 transition-opacity",
                        event.attendance.unmarked > 0
                          ? "bg-warning/10 text-warning border-warning/30"
                          : "bg-muted text-muted-foreground border-border"
                      )}
                      title={event.attendance.unmarked > 0 ? "Attendance marking incomplete" : "Fully marked"}
                    >
                      marked {event.attendance.present + event.attendance.no_show}/{event.attendance.confirmedTickets}
                    </Link>
                  )}
                </div>
              </TableCell>
              <TableCell className="text-right py-1 whitespace-nowrap">
                {displayEventTime(event.start_time, event.timezone)}
              </TableCell>
              <TableCell className="py-1 whitespace-nowrap max-w-[150px] truncate">
                {event.venue}
              </TableCell>
              <TableCell className="text-right py-1 font-medium">
                {event.booked || 0}
              </TableCell>
              <TableCell className="text-right py-1">
                {event.capacity}
              </TableCell>
              <TableCell className="text-right py-1">
                £{event.price}
              </TableCell>
              <TableCell className="py-1">
                <div className="flex flex-wrap gap-1 max-w-[180px]">
                  {event.tags?.slice(0, 3).map((tag: string) => (
                    <Badge
                      key={`${event.id}-${tag}`}
                      variant="secondary"
                      className="text-xs flex items-center gap-0.5 px-1.5 py-0"
                    >
                      {tag}
                      <X
                        className="h-2.5 w-2.5 cursor-pointer hover:text-destructive"
                        onClick={() => onDeleteTag(event.id, tag)}
                      />
                    </Badge>
                  ))}
                  {event.tags && event.tags.length > 3 && (
                    <Badge variant="outline" className="text-xs px-1.5 py-0">
                      +{event.tags.length - 3}
                    </Badge>
                  )}
                </div>
              </TableCell>
              {currentStatus === "canceled" && (
                <TableCell className="py-1 text-muted-foreground text-xs max-w-[120px] truncate">
                  {event.canceled_reason || "No reason"}
                </TableCell>
              )}
              <TableCell className="py-1">
                <div className="flex gap-1 justify-center">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => onEditEvent(event)}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "h-7 w-7",
                      event.is_archived
                        ? "text-success hover:text-success/80"
                        : "text-info hover:text-info/80"
                    )}
                    onClick={() => onToggleArchiveEvent(event.id, !event.is_archived)}
                  >
                    {!event.is_archived && <Archive className="w-3.5 h-3.5" />}
                    {event.is_archived && <ArchiveRestore className="w-3.5 h-3.5" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "h-7 w-7",
                      event.is_canceled
                        ? "text-success hover:text-success/80"
                        : "text-destructive hover:text-destructive/80"
                    )}
                    onClick={() => handleToggleCancelEvent(event.id, !event.is_canceled)}
                  >
                    {!event.is_canceled && <Trash2 className="w-3.5 h-3.5" />}
                    {event.is_canceled && <Undo2 className="w-3.5 h-3.5" />}
                  </Button>
                  {event.start_time && new Date(event.start_time).getTime() < now && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-secondary hover:text-secondary/80"
                      onClick={() => setFollowupsEventId(event.id)}
                      title="Process first-timer follow-ups"
                    >
                      <Sparkles className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
            <TableRow key={`${event.id}-bookings`} className="border-b-0">
              <TableCell colSpan={currentStatus === "canceled" ? 10 : 9} className="p-0">
                <AdminEventBookings event={event} />
              </TableCell>
            </TableRow>
          </>
        ))}

        {/* Empty state */}
        {isEmpty && (
          <TableRow>
            <TableCell colSpan={currentStatus === "canceled" ? 10 : 9} className="text-center text-muted-foreground py-8">
              No events found
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
    {followupsEventId && (
      <FirstTimerFollowupsDialog
        eventId={followupsEventId}
        open={!!followupsEventId}
        onOpenChange={(o) => !o && setFollowupsEventId(null)}
      />
    )}
    </>
  );
};

export default EventListTable;
